import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Wrench, Lightbulb, GraduationCap, Pencil } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useManufacturers from "@/hooks/useManufacturers";
import { CatalogBrowse } from "@/components/catalog/CatalogBrowse";
import { ProductManager } from "@/components/catalog/ProductManager";
import { CompanyManager } from "@/components/catalog/CompanyManager";
import { CategoryManager } from "@/components/catalog/CategoryManager";
import { ServiceManager } from "@/components/catalog/ServiceManager";
import { ProductTypeManager } from "@/components/catalog/ProductTypeManager";

const ENTITY_DEFS = {
  services: { label: "Services", icon: Wrench, fields: ["name", "type", "manufacturer_id", "is_training", "description", "scope", "deliverables", "duration", "unit_price", "currency", "uom", "benefits"], list: ["deliverables"] },
  solutions: { label: "Solutions", icon: Lightbulb, fields: ["name", "problem", "products_used", "services_used", "outcome", "roi"], list: ["products_used", "services_used"] },
};

function ItemDialog({ open, onOpenChange, entity, onSaved, initial = null }) {
  const def = ENTITY_DEFS[entity];
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const mfrs = useManufacturers();

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const seed = { ...initial };
      def.list.forEach((k) => {
        if (Array.isArray(seed[k])) seed[k] = seed[k].join("\n");
      });
      setForm(seed);
    } else {
      setForm({});
    }
  }, [open, entity, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      def.list.forEach((k) => {
        const v = payload[k];
        if (typeof v === "string") payload[k] = v.split("\n").map((s) => s.trim()).filter(Boolean);
      });
      if (entity === "services" && payload.unit_price !== undefined && payload.unit_price !== "") {
        const cleaned = String(payload.unit_price).replace(/[^0-9.]/g, "");
        payload.unit_price = cleaned ? parseFloat(cleaned) : null;
      }
      if (entity === "services") payload.is_training = !!payload.is_training;
      if (isEdit) {
        await api.patch(`/${entity}/${initial.id}`, payload);
        toast.success(`${def.label.slice(0, -1)} updated`);
      } else {
        await api.post(`/${entity}`, payload);
        toast.success(`${def.label.slice(0, -1)} added`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${entity}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">
            {isEdit ? `Edit ${def.label.slice(0, -1).toLowerCase()}` : `New ${def.label.slice(0, -1).toLowerCase()}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {def.fields.map((f) => {
            if (f === "is_training") {
              return (
                <div key={f} className="flex items-center justify-between p-3 rounded-2xl border border-border/60 bg-muted/20">
                  <div>
                    <Label className="text-xs font-semibold">This is a Training programme</Label>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Tick this to make the item appear under Category = Training in Quotations and the Training tab here.</p>
                  </div>
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-primary"
                    checked={!!form.is_training}
                    onChange={(e) => set("is_training", e.target.checked)}
                    data-testid="svc-is-training-toggle"
                  />
                </div>
              );
            }
            if (f === "manufacturer_id") {
              return (
                <div key={f}>
                  <Label className="text-xs font-semibold">Company (OEM)</Label>
                  <Select value={form.manufacturer_id || "__none"} onValueChange={(v) => set("manufacturer_id", v === "__none" ? "" : v)}>
                    <SelectTrigger className="rounded-2xl h-11 mt-1.5" data-testid="svc-supplier-select"><SelectValue placeholder="Pick a company" /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="__none">Unassigned</SelectItem>
                      {mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            const isList = def.list.includes(f);
            const isLong = ["description", "scope", "outcome", "problem", "roi", "benefits"].includes(f);
            const label = f.replace(/_/g, " ");
            return (
              <div key={f}>
                <Label className="text-xs font-semibold capitalize">{label}{isList ? " (one per line)" : ""}</Label>
                {isList || isLong ? (
                  <Textarea className="rounded-2xl mt-1.5" rows={isList ? 3 : 2} value={form[f] || ""} onChange={(e) => set(f, e.target.value)} data-testid={`prod-${f}-input`} />
                ) : (
                  <Input className="rounded-2xl h-11 mt-1.5" value={form[f] || ""} onChange={(e) => set(f, e.target.value)} data-testid={`prod-${f}-input`} />
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-2xl" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-2xl" data-testid="prod-save-button">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemList({ entity, items, onChanged, canEdit, trainingView = false }) {
  const def = ENTITY_DEFS[entity];
  const Icon = trainingView ? GraduationCap : def.icon;
  const [editing, setEditing] = useState(null);
  const mfrs = useManufacturers();
  const mfrLookup = mfrs.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});

  const fmtPrice = (v, currency) => {
    if (v === null || v === undefined || v === "") return null;
    const num = typeof v === "number" ? v : parseFloat(String(v).replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(num)) return null;
    if (!currency || currency === "INR") return fmtINR(num);
    return `${currency} ${num.toLocaleString()}`;
  };

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((it) => {
          const price = fmtPrice(it.unit_price, it.currency);
          const isTraining = entity === "services" && !!it.is_training;
          return (
            <Card key={it.id} className="rounded-3xl border-border/60 shadow-soft card-lift cursor-pointer" onClick={() => canEdit && setEditing(it)} data-testid={`${entity}-card-${it.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-2xl grid place-items-center shrink-0 ${isTraining ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-display font-extrabold text-base truncate">{it.name}</p>
                      {isTraining && !trainingView && (
                        <Badge variant="outline" className="text-[10px] font-semibold bg-amber-50 text-amber-800 border-amber-200" data-testid={`svc-training-badge-${it.id}`}>Training</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{it.type || ""}</p>
                  </div>
                  {price && (
                    <div className="shrink-0 text-right">
                      <p className="font-display font-black text-base text-primary-700 leading-tight">{price}</p>
                      {it.uom && <p className="text-[10px] uppercase tracking-wider text-muted-foreground">per {it.uom}</p>}
                    </div>
                  )}
                </div>
                {it.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{it.description}</p>}
                {entity === "services" && it.manufacturer_id && (
                  <p className="text-xs text-muted-foreground mt-2.5">
                    <span className="font-semibold text-foreground/80">Company:</span> {mfrLookup[it.manufacturer_id]?.name || "—"}
                  </p>
                )}
                {def.list.flatMap((k) => (it[k] || []).slice(0, 3).map((v, i) => (
                  <span key={`${k}-${i}`} className="inline-block px-2 py-0.5 mt-2 mr-1 text-[10px] font-semibold rounded-full bg-muted text-muted-foreground">{v}</span>
                )))}
                {canEdit && (
                  <div className="mt-3 pt-2 border-t border-border/40 flex items-center justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(it); }} className="rounded-xl h-7 text-xs" data-testid={`${entity}-edit-${it.id}`}>
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ItemDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        entity={entity}
        initial={editing}
        onSaved={() => { setEditing(null); onChanged?.(); }}
      />
    </>
  );
}

export default function Products() {
  const { user } = useAuth();
  const [tab, setTab] = useState("catalog");
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogEntity, setDialogEntity] = useState(null); // "services" | "solutions" | "training"

  const canAdd = (user?.permissions || []).includes("add_products");
  const canManageMasters = ["admin", "manager"].includes(user?.role);

  const loadAll = async () => {
    const [p, s, so, pc] = await Promise.all([
      api.get("/products"),
      api.get("/services"),
      api.get("/solutions"),
      api.get("/product-categories").catch(() => ({ data: [] })),
    ]);
    setProducts(p.data || []);
    setServices(s.data || []);
    setSolutions(so.data || []);
    setCategories(pc.data || []);
  };
  useEffect(() => { loadAll(); }, []);

  const trainings = services.filter((s) => s.is_training);
  const pureServices = services.filter((s) => !s.is_training);
  const productCounts = products.reduce((a, p) => { if (p.manufacturer_id) a[p.manufacturer_id] = (a[p.manufacturer_id] || 0) + 1; return a; }, {});

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1280px] mx-auto">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">Catalog</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Products & Services.</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">One central catalog powering the browse view and the quotation picker — manage companies, categories, products, services and training here.</p>
        </div>
        {canAdd && tab === "solutions" && (
          <Button onClick={() => setDialogEntity("solutions")} className="rounded-2xl tap" data-testid="new-solutions-button">
            <Plus className="h-4 w-4 mr-1" /> New solution
          </Button>
        )}
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="overflow-x-auto -mx-1 px-1 mb-5">
          <TabsList className="rounded-2xl w-max">
            <TabsTrigger value="catalog" className="rounded-xl" data-testid="tab-catalog">Catalog</TabsTrigger>
            <TabsTrigger value="manage" className="rounded-xl" data-testid="tab-products-manage">Products ({products.length})</TabsTrigger>
            <TabsTrigger value="services" className="rounded-xl" data-testid="tab-services">Services ({pureServices.length})</TabsTrigger>
            <TabsTrigger value="training" className="rounded-xl" data-testid="tab-training">Training ({trainings.length})</TabsTrigger>
            {canManageMasters && <TabsTrigger value="companies" className="rounded-xl" data-testid="tab-companies">Companies</TabsTrigger>}
            {canManageMasters && <TabsTrigger value="types" className="rounded-xl" data-testid="tab-types">Types</TabsTrigger>}
            {canManageMasters && <TabsTrigger value="categories" className="rounded-xl" data-testid="tab-categories">Categories</TabsTrigger>}
            <TabsTrigger value="solutions" className="rounded-xl" data-testid="tab-solutions">Solutions ({solutions.length})</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="catalog">
          <CatalogBrowse products={products} categories={categories} />
        </TabsContent>

        <TabsContent value="manage">
          <ProductManager products={products} categories={categories} canManage={canAdd} onChanged={loadAll} />
        </TabsContent>

        <TabsContent value="services">
          <ServiceManager items={pureServices} isTraining={false} categories={categories} canManage={canAdd} onChanged={loadAll} />
        </TabsContent>

        <TabsContent value="training">
          <ServiceManager items={trainings} isTraining categories={categories} canManage={canAdd} onChanged={loadAll} />
        </TabsContent>

        {canManageMasters && (
          <TabsContent value="types">
            <ProductTypeManager canManage={canManageMasters} />
          </TabsContent>
        )}

        {canManageMasters && (
          <TabsContent value="companies">
            <CompanyManager canManage={canManageMasters} productCounts={productCounts} />
          </TabsContent>
        )}
        {canManageMasters && (
          <TabsContent value="categories">
            <CategoryManager canManage={canManageMasters} onChanged={loadAll} />
          </TabsContent>
        )}

        <TabsContent value="solutions">
          {solutions.length === 0
            ? <Card className="rounded-3xl border-border/60 shadow-soft"><CardContent className="p-12 text-center text-sm text-muted-foreground">No solutions added yet.</CardContent></Card>
            : <ItemList entity="solutions" items={solutions} onChanged={loadAll} canEdit={canAdd} />}
        </TabsContent>
      </Tabs>

      <ItemDialog
        open={!!dialogEntity}
        onOpenChange={(v) => !v && setDialogEntity(null)}
        entity={dialogEntity === "training" ? "services" : (dialogEntity || "services")}
        initial={dialogEntity === "training" ? { is_training: true } : null}
        onSaved={loadAll}
      />
    </div>
  );
}
